import { type Style } from '@/domain/model';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * The properties panel.
 *
 * Presentation only: it renders the style it is handed and reports what the user asked
 * for. It never reads the document, so "restyle the selection" and "set the style for the
 * next thing I draw" are the same call from its point of view — the controller decides
 * which one applies.
 *
 * ## Mixed values
 *
 * With several entities selected, a field they disagree on arrives **absent** rather than
 * holding the first entity's value. The panel renders that as a blank or a "khác nhau" label.
 * Showing one entity's colour as though it were everyone's is how a user ends up
 * believing they changed something they did not.
 */

const STROKE_PRESETS = [
  '#1a1a1a',
  '#1d4ed8',
  '#b91c1c',
  '#0f766e',
  '#7c3aed',
  '#c2410c',
  '#8a8a99',
] as const;

const LINE_STYLES = [
  { value: 'solid', label: 'Nét liền', preview: '——' },
  { value: 'dashed', label: 'Nét đứt', preview: '– –' },
  { value: 'dotted', label: 'Nét chấm', preview: '· ·' },
] as const;

const POINT_STYLES = [
  { value: 'dot', label: 'Chấm tròn' },
  { value: 'circle', label: 'Vòng tròn' },
  { value: 'square', label: 'Vuông' },
  { value: 'cross', label: 'Chữ X' },
  { value: 'plus', label: 'Dấu cộng' },
] as const;

/**
 * Which ends wear an arrowhead.
 *
 * Labelled by what the mark *means* rather than by which end it is on: "→" and "←" would make a
 * reader work out which end of their own segment is which, and the segment's `a` and `b` are not
 * something the drawing shows. `Đầu` and `Cuối` say it in the order the object was drawn.
 *
 * A **ray** is not in this list and cannot be. Its arrow is what tells it from a segment, and an
 * object that can be drawn as a different object is not a setting worth having.
 */
const ARROW_ENDS = [
  { value: 'none', label: 'Không' },
  { value: 'end', label: 'Cuối' },
  { value: 'start', label: 'Đầu' },
  { value: 'both', label: 'Hai đầu' },
] as const;

const FILL_STYLES = [
  { value: 'none', label: 'Không tô' },
  { value: 'solid', label: 'Tô đặc' },
  { value: 'hatch', label: 'Gạch chéo' },
] as const;

export interface PropertiesPanelProps {
  /** Fields the selection agrees on. An absent field means the values differ. */
  readonly style: Partial<Style>;
  /** False when the panel is editing the style for entities not yet created. */
  readonly appliesToSelection: boolean;
  readonly selectionCount: number;
  readonly onChange: (patch: Partial<Style>) => void;
}

export function PropertiesPanel({
  style,
  appliesToSelection,
  selectionCount,
  onChange,
}: PropertiesPanelProps) {
  /*
   * The section owns its own fold, so nothing is kept here.
   *
   * This panel used to carry its own card chrome, its own corner and its own collapse button.
   * All three now come from `PanelSection`, which is what makes six panels read as one rail
   * rather than six things that happened to be written by the same person.
   */
  return (
    <PanelSection
      title="Kiểu vẽ"
      badge={appliesToSelection ? `${String(selectionCount)} đối tượng` : 'cho vật mới'}
    >
      <Field label="Màu nét">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {STROKE_PRESETS.map((colour) => (
            <button
              key={colour}
              type="button"
              title={colour}
              onClick={() => {
                onChange({ stroke: colour });
              }}
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                background: colour,
                border:
                  style.stroke === colour
                    ? '2px solid #111827'
                    : '1px solid var(--es-hairline-strong)',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
          <input
            type="color"
            aria-label="Màu tuỳ chọn"
            value={style.stroke ?? '#000000'}
            onChange={(event) => {
              onChange({ stroke: event.target.value });
            }}
            style={{
              width: 24,
              height: 22,
              padding: 0,
              border: '1px solid var(--es-hairline-strong)',
              borderRadius: 4,
              background: 'none',
              cursor: 'pointer',
            }}
          />
        </div>
        {style.stroke === undefined && <Mixed />}
      </Field>

      <Field label="Kiểu nét">
        <SegmentedControl
          options={LINE_STYLES.map((entry) => ({
            value: entry.value,
            label: entry.preview,
            title: entry.label,
          }))}
          value={style.lineStyle}
          onSelect={(value) => {
            onChange({ lineStyle: value as Style['lineStyle'] });
          }}
        />
      </Field>

      <Field
        label={`Độ dày${style.strokeWidth === undefined ? '' : ` ${String(style.strokeWidth)}`}`}
      >
        <input
          type="range"
          min={0.5}
          max={8}
          step={0.5}
          value={style.strokeWidth ?? 1.5}
          onChange={(event) => {
            onChange({ strokeWidth: Number(event.target.value) });
          }}
          style={{ width: '100%' }}
        />
        {style.strokeWidth === undefined && <Mixed />}
      </Field>

      <Field label="Kiểu điểm">
        <SegmentedControl
          options={POINT_STYLES.map((entry) => ({
            value: entry.value,
            label: entry.label,
            title: entry.label,
          }))}
          value={style.pointStyle}
          onSelect={(value) => {
            onChange({ pointStyle: value as Style['pointStyle'] });
          }}
        />
      </Field>

      <Field label="Mũi tên">
        <SegmentedControl
          options={ARROW_ENDS.map((entry) => ({
            value: entry.value,
            label: entry.label,
            title: entry.label,
          }))}
          value={style.arrows}
          onSelect={(value) => {
            onChange({ arrows: value as Style['arrows'] });
          }}
        />
      </Field>

      <Field label="Màu nền">
        <SegmentedControl
          options={FILL_STYLES.map((entry) => ({
            value: entry.value,
            label: entry.label,
            title: entry.label,
          }))}
          value={style.fillStyle}
          onSelect={(value) => {
            const fillStyle = value as Style['fillStyle'];
            // Choosing a fill mode with no colour set would silently do nothing, so a
            // default is supplied at the same moment.
            onChange(
              fillStyle !== 'none' && (style.fill === undefined || style.fill === 'transparent')
                ? { fillStyle, fill: style.stroke ?? '#1d4ed8' }
                : { fillStyle }
            );
          }}
        />
        {style.fillStyle !== undefined && style.fillStyle !== 'none' && (
          <input
            type="color"
            aria-label="Màu nền tuỳ chọn"
            value={style.fill === 'transparent' ? '#1d4ed8' : (style.fill ?? '#1d4ed8')}
            onChange={(event) => {
              onChange({ fill: event.target.value });
            }}
            style={{
              marginTop: 6,
              width: '100%',
              height: 22,
              padding: 0,
              border: '1px solid var(--es-hairline-strong)',
              borderRadius: 4,
              background: 'none',
              cursor: 'pointer',
            }}
          />
        )}
      </Field>

      <Field
        label={`Độ mờ${style.opacity === undefined ? '' : ` ${String(Math.round(style.opacity * 100))}%`}`}
      >
        <input
          type="range"
          min={0.1}
          max={1}
          step={0.05}
          value={style.opacity ?? 1}
          onChange={(event) => {
            onChange({ opacity: Number(event.target.value) });
          }}
          style={{ width: '100%' }}
        />
        {style.opacity === undefined && <Mixed />}
      </Field>
    </PanelSection>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="es-eyebrow">{label}</span>
      {children}
    </label>
  );
}

function Mixed() {
  return <span style={{ color: colour.faint, fontSize: 10, fontStyle: 'italic' }}>khác nhau</span>;
}

function SegmentedControl({
  options,
  value,
  onSelect,
}: {
  readonly options: readonly { value: string; label: string; title: string }[];
  readonly value: string | undefined;
  readonly onSelect: (value: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            title={option.title}
            onClick={() => {
              onSelect(option.value);
            }}
            className={active ? 'es-btn es-btn--on' : 'es-btn'}
            aria-pressed={active}
            style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
